import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Pill, Button } from "../../components/common/Primitives";
import AdminListSearch from "../../components/common/AdminListSearch";
import OnboardModal from "../../components/common/OnboardModal";
import AddToDealRoomModal from "../../components/admin/AddToDealRoomModal";
import AdminApprovalBadge from "../../components/common/AdminApprovalBadge";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { ShieldCheck, ArrowRight, UserPlus, DoorOpen } from "lucide-react";

const statuses = ["Draft", "Submitted", "Under Review", "More Information Required", "Approved", "Rejected", "Investor Visible", "Hidden", "Paused"];

const KYC_ACTIONS = [
  { key: "Approved", label: "Approve", tone: "success" },
  { key: "More Information Required", label: "More Info", tone: "warn" },
  { key: "Rejected", label: "Reject", tone: "danger" },
];

export default function AdminStartups() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [kycBusy, setKycBusy] = useState(null); // startup_id being updated
  const [query, setQuery] = useState("");
  const [showOnboard, setShowOnboard] = useState(false);
  const [dealRoomFor, setDealRoomFor] = useState(null); // pinned startup obj
  const [visibilityFor, setVisibilityFor] = useState(null); // startup row for "List on Curated Deals" dialog
  const [forceApproveFor, setForceApproveFor] = useState(null); // startup row for super-admin force approve
  const [kycMoreInfoFor, setKycMoreInfoFor] = useState(null); // { row, status } — for MoreInfo/Reject reason capture

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.startup_name, r.founder_email, r.sector, r.business_stage,
                   r.city, r.industry, r.status, r.kyc_status, r.legal_entity_name]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const load = async () => {
    try {
      const [r, t] = await Promise.all([api.get("/admin/startups"), api.get("/admin/team")]);
      setRows(r.data || []);
      setTeam(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/startups/${id}/status`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const forceApprove = (row) => setForceApproveFor(row);
  const confirmForceApprove = async ({ reason }) => {
    const row = forceApproveFor;
    if (!row) return;
    try {
      await api.post(`/admin/startups/${row.startup_id}/force-approve`, { note: reason, override_kyc: true });
      toast.success("Force-approved.");
      setForceApproveFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setKyc = async (id, status) => {
    if (status !== "Approved") {
      const row = rows.find((r) => r.startup_id === id);
      setKycMoreInfoFor({ row, status });
      return;
    }
    setKycBusy(id);
    try {
      await api.post(`/admin/startups/${id}/kyc-status`, { status });
      toast.success(`KYC marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setKycBusy(null);
    }
  };

  const confirmKycNote = async ({ reason }) => {
    const { row, status } = kycMoreInfoFor || {};
    if (!row) return;
    setKycBusy(row.startup_id);
    try {
      await api.post(`/admin/startups/${row.startup_id}/kyc-status`, { status, note: reason || undefined });
      toast.success(`KYC marked ${status}.`);
      setKycMoreInfoFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setKycBusy(null);
    }
  };

  const assignPoc = async (id, user_id) => {
    if (!user_id) return;
    try {
      await api.post(`/admin/startups/${id}/assign-poc`, { user_id });
      toast.success("POC assigned.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleMarketplaceVisibility = async (row) => {
    if (row.admin_force_visible) {
      try {
        await api.post(`/admin/startups/${row.startup_id}/marketplace-visibility`, { visible: false });
        toast.success("Removed from Curated Deals.");
        load();
      } catch (e) { toast.error(formatApiError(e)); }
    } else {
      setVisibilityFor(row);
    }
  };

  const confirmVisibility = async ({ reason }) => {
    const row = visibilityFor;
    try {
      await api.post(`/admin/startups/${row.startup_id}/marketplace-visibility`, { visible: true, reason });
      toast.success("Now visible in Curated Deals.");
      setVisibilityFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell
      title="Startups"
      subtitle="ADMIN · APPLICATIONS"
      actions={
        <div className="flex items-center gap-2">
          {!user?.demo_account && (
            <button
              onClick={() => setShowOnboard(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-700 text-white text-xs font-heading font-semibold hover:bg-emerald-800 transition-colors"
              data-testid="btn-onboard-startup"
            >
              <UserPlus size={13} /> Onboard startup
            </button>
          )}
          <Link
            to="/admin/kyc-queue"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-navy text-white text-xs font-heading font-semibold hover:bg-[#256E58] transition-colors"
            data-testid="btn-open-kyc-queue"
          >
            <ShieldCheck size={13} /> KYC Queue <ArrowRight size={12} />
          </Link>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <OnboardModal
        open={showOnboard}
        onClose={() => setShowOnboard(false)}
        kind="startup"
        actorRole={user?.role}
        onDone={() => load()}
      />
      <AddToDealRoomModal
        open={!!dealRoomFor}
        onClose={() => setDealRoomFor(null)}
        mode="startup"
        pinned={dealRoomFor}
        onCreated={() => load()}
      />
      <ConfirmReasonDialog
        open={!!visibilityFor}
        title={`List ${visibilityFor?.startup_name || visibilityFor?.founder_email || "this startup"} on Curated Deals?`}
        description="Investors will see a KYC Pending badge until KYC is approved. Every force-listing is audit-logged."
        confirmLabel="List on Curated Deals"
        tone="gold"
        reasonPlaceholder="Why is this startup being featured now?"
        onConfirm={confirmVisibility}
        onClose={() => setVisibilityFor(null)}
        testId="startups-visibility-dialog"
      />
      <ConfirmReasonDialog
        open={!!forceApproveFor}
        title={`Force-approve ${forceApproveFor?.startup_name || forceApproveFor?.founder_email || "this startup"}?`}
        description="SUPER-ADMIN OVERRIDE — flips both profile status AND KYC status to Approved instantly, bypassing document review and Sandbox verification. Logged with your email."
        confirmLabel="Force Approve"
        tone="danger"
        reasonPlaceholder="Reason (visible in audit log + founder email)"
        onConfirm={confirmForceApprove}
        onClose={() => setForceApproveFor(null)}
        testId="startups-force-approve-dialog"
      />
      <ConfirmReasonDialog
        open={!!kycMoreInfoFor}
        title={`KYC → ${kycMoreInfoFor?.status || ""}`}
        description={`Add a note for ${kycMoreInfoFor?.row?.startup_name || "the founder"} (visible in the email + audit log).`}
        confirmLabel={`Mark ${kycMoreInfoFor?.status || "KYC"}`}
        tone={kycMoreInfoFor?.status === "Rejected" ? "danger" : "gold"}
        reasonRequired={false}
        reasonLabel="Note (optional — leave blank to skip)"
        reasonPlaceholder="e.g. Bank statement was for a different entity."
        onConfirm={confirmKycNote}
        onClose={() => setKycMoreInfoFor(null)}
        testId="startups-kyc-note-dialog"
      />
      <AdminListSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by name, sector, founder email, city…"
        testId="admin-startups-search"
        count={filtered.length}
      />
      <div className="glass overflow-x-auto rounded-lg">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
            <tr className="border-b border-navy/10">
              <th className="text-left px-4 py-3">Startup</th>
              <th className="text-left px-4 py-3">Sector</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Ask</th>
              <th className="text-left px-4 py-3">KYC</th>
              <th className="text-left px-4 py-3">KYC Action</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">POC</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const kycTone = r.kyc_status === "Approved" ? "success"
                : r.kyc_status === "Rejected" ? "danger"
                : r.kyc_status === "Under Review" ? "warn" : "muted";
              return (
                <tr key={r.startup_id} className="border-b border-navy/5" data-testid={`admin-startup-${r.startup_id}`}>
                  <td className="px-4 py-3 text-ink font-heading">{r.startup_name || r.founder_email || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft">{r.sector || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft">{r.business_stage || "—"}</td>
                  <td className="px-4 py-3 text-ink font-mono text-xs">{r.funding_ask ? `₹${(r.funding_ask / 1e7).toFixed(2)}Cr` : "—"}</td>
                  <td className="px-4 py-3"><Pill tone={kycTone}>{r.kyc_status || "—"}</Pill>
                    <div className="mt-1"><AdminApprovalBadge entity={r} size="sm" testId={`admin-approval-${r.startup_id}`} /></div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) setKyc(r.startup_id, v);
                        e.target.value = "";
                      }}
                      disabled={kycBusy === r.startup_id}
                      className="text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none disabled:opacity-50"
                      data-testid={`kyc-action-select-${r.startup_id}`}
                    >
                      <option value="">— Set KYC —</option>
                      {KYC_ACTIONS.map((a) => (
                        <option key={a.key} value={a.key} data-testid={`kyc-action-${a.key}-${r.startup_id}`}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3"><Pill tone={r.status === "Approved" ? "success" : r.status === "Rejected" ? "danger" : "warn"}>{r.status || "—"}</Pill></td>
                  <td className="px-4 py-3">
                    <select
                      value={r.assigned_poc_user_id || ""}
                      onChange={(e) => assignPoc(r.startup_id, e.target.value)}
                      className="text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none"
                      data-testid={`startup-poc-select-${r.startup_id}`}
                    >
                      <option value="">— Unassigned —</option>
                      {team.map((t) => {
                        const label = `${t.name || t.email} · ${t.role}`;
                        return <option key={t.user_id} value={t.user_id}>{label}</option>;
                      })}
                    </select>
                  </td>
                  <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                    <Button variant="dark" onClick={() => setStatus(r.startup_id, "Approved")} data-testid={`approve-startup-${r.startup_id}`}>Approve</Button>
                    <Button variant="dark" onClick={() => setStatus(r.startup_id, "Rejected")} data-testid={`reject-startup-${r.startup_id}`}>Reject</Button>
                    <Button
                      variant="dark"
                      onClick={() => setDealRoomFor(r)}
                      className="!bg-emerald-700 hover:!bg-emerald-800"
                      data-testid={`open-deal-room-startup-${r.startup_id}`}
                      title="Open a deal room between this startup and any investor"
                    >
                      <DoorOpen size={12} className="inline mr-1" />Deal Room
                    </Button>
                    <Button
                      variant="dark"
                      onClick={() => toggleMarketplaceVisibility(r)}
                      className={r.admin_force_visible ? "!bg-rose-700 hover:!bg-rose-800" : "!bg-[#8A6600] hover:!bg-[#6B4E00]"}
                      data-testid={`toggle-curated-visibility-${r.startup_id}`}
                      title={r.admin_force_visible ? "Remove from Curated Deals" : "Force-list on Curated Deals (bypass KYC gate)"}
                    >
                      {r.admin_force_visible ? "Hide from Deals" : "List on Deals"}
                    </Button>
                    {user?.role === "super_admin" && !user?.demo_account && (r.status !== "Approved" || r.kyc_status !== "Approved") && (
                      <Button
                        variant="dark"
                        onClick={() => forceApprove(r)}
                        className="!bg-amber-600 hover:!bg-amber-700"
                        data-testid={`force-approve-startup-${r.startup_id}`}
                        title="Super-admin override: skip KYC + docs review"
                      >
                        ⚡ Force Approve
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-ink-faint">
                {rows.length === 0 ? "No startups yet." : "No startups match your search."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardShell>
  );
}
